// Whale GLB asset loader.
// Loads the model once, keeps it hidden until the scene is ready to reveal it.
// On failure, transitions to LOAD_ERROR.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { appConfig } from '../config/appConfig';
import { whaleLoadFailed } from '../state/appState';
import { logger } from '../utils/logger';

let whaleObject: THREE.Group | null = null;
let animationClips: THREE.AnimationClip[] = [];

/**
 * Loads the whale GLB, then adds the model (hidden) to the provided scene.
 * Resolves when the asset is ready; rejects and
 * transitions to LOAD_ERROR on failure.
 */
export function loadWhale(scene: THREE.Scene): Promise<THREE.Group> {
  if (whaleObject) return Promise.resolve(whaleObject);

  return new Promise<THREE.Group>((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      appConfig.whaleAssetPath,
      (gltf) => {
        const group = gltf.scene;
        group.userData.unitScale = group.scale.x;

        group.visible = false;
        group.traverse((obj) => {
          // Disable frustum culling so the whale stays rendered when the
          // camera looks away after reveal.
          obj.frustumCulled = false;
          if (obj instanceof THREE.Mesh) {
            const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
            for (const material of materials) {
              // GLB textures should already be sRGB, but keep the color space
              // explicit for consistent tone mapping.
              if ('map' in material && material.map instanceof THREE.Texture) {
                material.map.colorSpace = THREE.SRGBColorSpace;
              }
            }
            // Enable shadow casting/receiving — no-ops when shadow maps are off.
            obj.castShadow = true;
            obj.receiveShadow = true;
          }
        });
        scene.add(group);
        whaleObject = group;
        animationClips = gltf.animations;
        logger.log('Whale asset loaded, clips:', animationClips.length);
        resolve(group);
      },
      undefined,
      (err) => {
        logger.error('Whale asset load failed:', err);
        whaleLoadFailed();
        reject(err);
      },
    );
  });
}

export function getWhaleObject(): THREE.Group | null {
  return whaleObject;
}

export function getWhaleAnimationClips(): THREE.AnimationClip[] {
  return animationClips;
}
